// import React from "react"
import i18n from "i18next"
import { initReactI18next } from "react-i18next"
import LanguageDetector from "i18next-browser-languagedetector"
import i18nCountries from "i18n-iso-countries"
import { cloneDeep } from "lodash"
import moment from "moment"
import "moment/locale/de"

import de from "./locales/de.json"
import en from "./locales/en.json"
import es from "./locales/es.json"
import fr from "./locales/fr.json"
import it from "./locales/it.json"
import pl from "./locales/pl.json"
import pt from "./locales/pt.json"
import ro from "./locales/ro.json"
import tr from "./locales/tr.json"
import zh from "./locales/zh.json"
import ko from "./locales/ko.json"
import sr from "./locales/sr.json"
import hr from "./locales/hr.json"
import cs from "./locales/cs.json"
import { isDevEnv } from "utils/env"

export interface Language {
  locale: string
  name: string
  icon: JSX.Element
}

export const FALLBACK_LANGUAGE = "en"

export const defaultTranslationModules = [
  { locale: "de", texts: de },
  { locale: "en", texts: en },
  { locale: "es", texts: es },
  { locale: "fr", texts: fr },
  { locale: "it", texts: it },
  { locale: "pl", texts: pl },
  { locale: "pt", texts: pt },
  { locale: "ro", texts: ro },
  { locale: "tr", texts: tr },
  { locale: "zh", texts: zh },
  { locale: "ko", texts: ko },
  { locale: "sr", texts: sr },
  { locale: "hr", texts: hr },
  { locale: "cs", texts: cs },
]
export const defaultLanguages = defaultTranslationModules.map((m) => m.locale)

const getHtmlLangAttribute = (): string | null => {
  // Get language from HTML tag lang attribute
  const htmlLang = document.documentElement.lang
  if (htmlLang) {
    return htmlLang.split("-")[0].toLowerCase()
  }
  return null
}
const getBrowserLanguage = (): string => {
  // @ts-ignore
  const userLang = navigator.language || navigator.userLanguage
  return userLang ? userLang.split("-")[0] : FALLBACK_LANGUAGE
}
const getDetectedLanguage = (): string => {
  // Priority:
  // 1. HTML lang attribute
  // 2. Browser language
  // 3. Fallback
  const htmlLang = getHtmlLangAttribute()
  if (htmlLang && defaultLanguages.includes(htmlLang)) {
    return htmlLang
  }

  const browserLang = getBrowserLanguage()
  if (browserLang && defaultLanguages.includes(browserLang)) {
    return browserLang
  }

  return FALLBACK_LANGUAGE
}
const detectedLanguage = getDetectedLanguage()

const resources = cloneDeep(
  Object.fromEntries(
    defaultTranslationModules.map((m) => [m.locale, { app: m.texts }])
  )
)

// register defaultLanguages for country names
defaultLanguages.forEach((locale) =>
  i18nCountries.registerLocale(
    require(`i18n-iso-countries/langs/${locale}.json`)
  )
)

i18n
  .use(LanguageDetector)

  // pass the i18n instance to react-i18next.
  .use(initReactI18next)

  // init i18next
  // for all options read: https://www.i18next.com/overview/configuration-options
  .init({
    resources,
    debug: isDevEnv(),
    ns: ["app"],
    defaultNS: "app",
    lng: detectedLanguage,
    fallbackLng: FALLBACK_LANGUAGE,
    interpolation: {
      escapeValue: false, // not needed for react as it escapes by default
    },
    react: {
      bindI18n: "languageChanged",
      bindI18nStore: "added",
    },
  })

moment.locale(i18n.language)

// Listen for language change events from parent website
window.addEventListener("languageChange", ((
  event: CustomEvent<{ language: string }>
) => {
  const newLang = event.detail.language
  if (
    newLang &&
    defaultLanguages.includes(newLang) &&
    newLang !== i18n.language
  ) {
    i18n.changeLanguage(newLang)
    moment.locale(newLang)
  }
}) as EventListener)

let SETUP_COMPLETED = {
  [FALLBACK_LANGUAGE]: false,
}
export const isSetup = (language: string) => SETUP_COMPLETED[language]
export const setup = (language: string) => {
  if (isSetup(language) === false) {
    i18nCountries.registerLocale(
      require(`i18n-iso-countries/langs/${language}.json`)
    )
  }
}

export default i18n
